import React from 'react';
import { ToneBadge } from '../../../../../components/ui/ToneBadge';
import { StatusBadge } from '../../../../../components/deliveries/StatusBadge';
import { moduleInactiveLabel, wmsOrderStateColor, wmsOrderStateLabel } from '../../../../../utils/inventoryLabels';
import { isOpenState } from '../../../../../features/wmsOrders/wmsOrdersUtils';

/**
 * The warehouse state of an order, and what qualifies it.
 *
 * Two things are said under the badge when they apply, because both make the
 * state misleading on its own: a module switched off for that client (the
 * order is seen, but nothing can be done with it here), and stock that did not
 * cover the whole order (it sits in "Ingresada" with goods on the shelf). The
 * second one only while there is still something to prepare: a cancelled or
 * dispatched order lacks nothing.
 *
 * Once the goods left, the warehouse state stops moving ("Despachada") while
 * the shipment goes on — picked up, on the way, delivered, returning. That is
 * the OMS's status, and it is read here under the badge so the trace does not
 * end at the door. While the order is still inside, the shipment's status is
 * behind the warehouse's own (the OMS sends to the courier at creation) and
 * would only confuse, so it is not drawn.
 *
 * @param {object} props
 * @param {object} props.order - A row of `wms/orders/`.
 */
export const OrderStateCell = ({ order }) => (
  <div className="flex flex-col items-start gap-1">
    <ToneBadge tone={wmsOrderStateColor(order.wms_state)} testId="order-state">
      {wmsOrderStateLabel(order.wms_state)}
    </ToneBadge>
    {!isOpenState(order.wms_state) && order.delivery_status && (
      <StatusBadge status={order.delivery_status} variant="inline" testId="order-delivery-status" />
    )}
    {order.module_active === false && (
      <span
        data-testid="order-module-off"
        title={`Módulo apagado: ${moduleInactiveLabel(order.module_inactive_reason)}. La orden se ve, pero acá no hay nada que se pueda hacer con ella.`}
        className="text-xs font-medium text-danger"
      >
        Módulo apagado
      </span>
    )}
    {order.module_active !== false && !order.processable && isOpenState(order.wms_state) && (
      <span
        data-testid="order-short-stock"
        title="No alcanzó el stock para reservar todo lo pedido. Se completa sola cuando entre mercadería."
        className="text-xs font-medium text-warning"
      >
        Sin stock suficiente
      </span>
    )}
  </div>
);
